/**
 * Correct an imported week's frozen total.
 *
 * An imported (backfill) week has no lines — its total was hand-typed off a
 * pre-portal spreadsheet during the 2026-07-09 import, and until now a typo in
 * one was unreachable from the app: recompute skips imported rows and Reopen
 * refuses them outright. The first time a PM needed one fixed (2026-08-19) it
 * took an ops-side D1 UPDATE. This is that operation, self-serve.
 *
 * THE REASON IS REQUIRED, and the copy says why rather than just marking the
 * field: the figure being replaced exists nowhere else once it is saved. There
 * are no lines to re-derive it from, so the audit row this writes is the whole
 * account of what changed. That is a fact worth one sentence on screen (RULES
 * 7.8's "load-bearing facts get a gate, not a footnote", scaled to a two-field
 * modal).
 *
 * A form modal, so `.pdf-modal-head` rather than the one-question
 * `.confirm-modal-head` family (RULES 1.4).
 */
import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { useBodyScrollLock } from '../lib/useBodyScrollLock';
import { formatMoneyCents, formatWeekRange } from '../lib/format';
import { rollupActionErrorMessage } from '../lib/rollupErrors';
import { parseAmountCents } from './pco/BucketRowEditor';
import { useToast } from './ToastProvider';

export function EditImportedTotalModal({
  rollupId,
  weekEndingDate,
  currentTotalCents,
  onCancel,
  onSaved,
}: {
  rollupId: string;
  weekEndingDate: string;
  currentTotalCents: number;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const { token } = useAuth();
  const toast = useToast();
  const [amountDollars, setAmountDollars] = useState((currentTotalCents / 100).toFixed(2));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Mounted only while open, so the lock is unconditional.
  useBodyScrollLock(true);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  const nextCents = parseAmountCents(amountDollars);
  const reasonGiven = reason.trim().length > 0;
  // Blank-required stays silent; only the disabled button says "not yet"
  // (RULES 7.2). The server enforces all three of these too.
  const unchanged = nextCents === currentTotalCents;
  const canSave = !busy && nextCents > 0 && reasonGiven && !unchanged;

  const save = async () => {
    if (!token || !canSave) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await api.rollups.editImportedTotal(token, rollupId, {
        invoice_total_cents: nextCents,
        reason: reason.trim(),
      });
      toast.push({
        variant: 'success',
        title: 'Total corrected',
        body: `Week ${formatWeekRange(weekEndingDate)} is now ${formatMoneyCents(
          res.invoice_total_cents,
        )}, was ${formatMoneyCents(res.previous_invoice_total_cents)}.`,
        durationMs: 6000,
      });
      await onSaved();
    } catch (e) {
      setErr(rollupActionErrorMessage(e, 'Could not save the correction.'));
      setBusy(false);
    }
  };

  return (
    <div
      className="pdf-modal-overlay"
      onClick={() => {
        if (!busy) onCancel();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Edit imported week total"
    >
      <div
        className="new-pco-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 460 }}
      >
        <div className="pdf-modal-head">
          <div className="pdf-modal-title">Edit total</div>
          <button
            type="button"
            className="pdf-modal-close"
            onClick={onCancel}
            disabled={busy}
            aria-label="Close"
          >
            <X size={14} strokeWidth={2} aria-hidden />
          </button>
        </div>
        <div className="new-pco-body">
          <p style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.5, margin: 0 }}>
            Week <strong>{formatWeekRange(weekEndingDate)}</strong> is imported history — it
            has no lines behind it, so this figure was entered by hand and is the only place
            it lives. Currently <strong>{formatMoneyCents(currentTotalCents)}</strong>.
          </p>

          <div className="new-pco-field">
            <label className="field-label">Corrected total</label>
            <div className="field-input-group">
              <span className="field-prefix" aria-hidden>
                $
              </span>
              <input
                className="field-input mono"
                inputMode="decimal"
                value={amountDollars}
                onChange={(e) => setAmountDollars(e.target.value)}
                autoFocus
                disabled={busy}
                aria-label="Corrected total in dollars"
              />
            </div>
          </div>

          <div className="new-pco-field">
            <label className="field-label">Reason</label>
            <input
              className="field-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={busy}
              // Same cap the server enforces. Here so it is discovered while
              // typing rather than through a rejected save.
              maxLength={500}
              placeholder="What was wrong with the old figure?"
            />
            {/* Not a nag — the field is required BECAUSE the old figure is
                about to stop existing anywhere. */}
            <p className="muted" style={{ fontSize: 12, margin: '4px 0 0', lineHeight: 1.45 }}>
              Recorded with your name against this week. Nothing else about the week changes —
              it stays imported, stays sent, and is not recalculated.
            </p>
          </div>

          {err && <div style={{ color: 'var(--danger)', fontSize: 12.5 }}>{err}</div>}

          <div className="new-pco-actions">
            <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="btn btn-cta" onClick={save} disabled={!canSave}>
              {busy ? 'Saving…' : 'Save correction'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
